import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RouterSwitcher } from './root/routerSwitcher';
import { Footer, TopBar } from './fragments';
import { Images, ToastFail, ToastSuccess, captureUtmParams } from 'utils';
import { getToken } from 'utils/crypto';
import { httpRequestsOnSuccessActions, UploadServices } from 'store';
import { getToken as getFirebaseToken, onMessage } from 'firebase/messaging';
import { messaging } from './firebase';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { toastStyles } from './components/toast/styles';
import { toast } from 'react-toastify';
import { Toast } from './components';
import useVersionCheck from './utils/hooks/useVersionCheck';

// Capture UTM parameters from the URL immediately so they are persisted
// in sessionStorage before any in-app navigation clears them.
captureUtmParams();

function App() {
    const { httpOnError, httpOnSuccess } = useSelector((state) => ({
        httpOnError: state.httpOnError,
        httpOnSuccess: state.httpOnSuccess,
    }));
    const classes = toastStyles();
    const dispatch = useDispatch();
    const route = useLocation();
    const token = getToken();
    const success = httpOnSuccess.length && httpOnSuccess[0].type;
    const error = httpOnError.length && httpOnError[0].type;
    const errorMessage = httpOnError.length && httpOnError[0].error;
    const toastSuccess = ToastSuccess(success);
    const toastFail = ToastFail(error, errorMessage);

    useVersionCheck(60000);

    const getUploads = () => {
        const uploading = localStorage.getItem('uploadingInProcess');
        return uploading ? JSON.parse(uploading) : [];
    };

    useEffect(() => {
        const uploads = getUploads();
        const unUploadedFiles = localStorage.getItem('removeUnUploaded');
        if (unUploadedFiles && uploads?.length > 0) {
            UploadServices.VideoAbortMultiple({
                videoList: uploads,
            })
                .then(() => {
                    localStorage.removeItem('uploadingInProcess');
                    localStorage.removeItem('removeUnUploaded');
                })
                .catch(() => {});
        }
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            const uploads = getUploads();
            if (uploads.length > 0) {
                localStorage.setItem('removeUnUploaded', 'true');
                event.preventDefault();
                event.returnValue = 'Upload in progress. Are you sure you want to leave?';
            }
        };

        const handleUnload = (event) => {
            const uploads = getUploads();
            if (uploads?.length === 0) return;
            if (uploads.length > 0) {
                localStorage.setItem('removeUnUploaded', 'true');
                event.preventDefault();
                event.returnValue = 'Upload in progress. Are you sure you want to leave?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handleUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handleUnload);
        };
    }, []);

    useEffect(() => {
        if (!!toastSuccess) {
            // if (!!success) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(success));
        }
    }, [toastSuccess]);

    // useEffect(() => {
    //     if (!!toastFail) {
    //         if (!!error) {
    // dispatch(httpRequestsOnErrorsActions.removeError(error));
    // }
    // }, [toastFail]);

    const checkBar = () => {
        return (
            route?.pathname === '/' ||
            route?.pathname === '/features' ||
            route?.pathname === '/about-us' ||
            route?.pathname === '/contact-us' ||
            route?.pathname === '/data-collection' ||
            route?.pathname === '/practice' ||
            route?.pathname === '/aba-billing-software' ||
            route?.pathname === '/managed-billing' ||
            route?.pathname === '/aba-scheduling-software' ||
            route?.pathname === '/help-center' ||
            route?.pathname === '/help-center/getting-started' ||
            route?.pathname === '/help-center/control-panel' ||
            route?.pathname === '/terms-and-conditions' ||
            route?.pathname === '/privacy-policy' ||
            /^\/qr\/[^/]+$/.test(route?.pathname)
        );
    };

    const dontShowFooter = () => {
        return route?.pathname === '/help-center';
    };

    const registerServiceWorkerAndRequestPermission = async () => {
        if (!messaging) return;
        if ('serviceWorker' in navigator) {
            try {
                const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                messaging.useServiceWorker(registration);
            } catch (error) {}
        }
    };

    const requestNotificationPermission = async () => {
        if (!messaging) return;
        try {
            const firebaseToken = await getFirebaseToken(messaging, { vapidKey: process.env.REACT_APP_VAPED });
            if (firebaseToken && token) {
                const deviceId = getDeviceId();
                axios
                    .patch(
                        '/staff/pnToken',
                        {
                            pnToken: firebaseToken,
                            deviceOS: 'WEB',
                            deviceId: deviceId,
                        },
                        { auth: true }
                    )
                    .then((res) => {})
                    .catch((err) => {});
            }
        } catch (error) {}
    };

    useEffect(() => {
        requestNotificationPermission().then((r) => {});
        registerServiceWorkerAndRequestPermission().then((r) => {});
    }, []);

    useEffect(() => {
        if (!messaging) return;
        const unsubscribe = onMessage(messaging, (payload) => {
            const data = payload?.data || {};
            if (data.type === 'CHAT_MESSAGE') {
                const title = payload?.notification?.title || 'New Message';
                const body = payload?.notification?.body || '';
                toast(
                    <div className={classes.toastWrapper}>
                        <p className={classes.toastText}>
                            <b>{title}</b> {body}
                        </p>
                    </div>
                );
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        getDeviceId();
    }, []);

    const getDeviceId = () => {
        let deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
            deviceId = uuidv4();
            localStorage.setItem('deviceId', deviceId);
        }
        return deviceId;
    };

    useEffect(() => {
        if (httpOnSuccess?.length) {
            httpOnSuccess?.forEach((item) => {
                const text = ToastSuccess(item?.type);
                if (text) {
                    toast(
                        <div className={classes.toastWrapper}>
                            <img src={Images.successIcon} alt="success" />
                            <p className={classes.toastText}>{text}</p>
                        </div>
                    );
                }
                dispatch(httpRequestsOnSuccessActions.removeSuccess(item?.type));
            });
        }
        // if (httpOnError?.length) {
        //     httpOnError?.forEach((item) => {
        //         const toastFail = ToastFail(item?.type, item?.error);
        //         if (toastFail) {
        //             toast(
        //                 <div className={classes.toastWrapper}>
        //                     <img src={Images.successIcon} alt="success" />
        //                     <p className={classes.toastText}>{toastFail}</p>
        //                 </div>,
        //             );
        //         }
        //         dispatch(httpRequestsOnErrorsActions.removeError(item?.type));
        //     });
        // }
    }, [httpOnSuccess]);

    return (
        <div>
            {checkBar() ? (
                <div className="App">
                    {!token && <TopBar />}
                    <RouterSwitcher />
                    {!token && !dontShowFooter() && <Footer />}
                </div>
            ) : (
                <RouterSwitcher />
            )}

            <Toast
                type={toastSuccess ? 'success' : toastFail ? 'error' : ''}
                // text={toastSuccess ? toastSuccess : toastFail ? toastFail : ''}
                text={toastFail ? toastFail : ''}
                info={toastFail ? !!toastFail : ''}
                // info={toastSuccess ? !!toastSuccess : toastFail ? !!toastFail : ''}
            />

            {/*<ToastContainer*/}
            {/*    className={`success ${classes.defaultToast}`}*/}
            {/*    position="top-center"*/}
            {/*    autoClose={3500}*/}
            {/*    hideProgressBar*/}
            {/*    newestOnTop={false}*/}
            {/*    closeOnClick*/}
            {/*    rtl={false}*/}
            {/*    pauseOnFocusLoss*/}
            {/*    draggable={false}*/}
            {/*    pauseOnHover={false}*/}
            {/*/>*/}
        </div>
    );
}

export default App;
